// Takes hidden metadata, location above all, out of pictures and videos without re-encoding them (#352). The Worker
// uses it on every upload; the web app uses it before an upload. It reads a file as a stream (`Open`), keeps only the
// structure, and returns a plan: byte ranges to cut, to overwrite or to blank. The caller applies the plan to bytes
// (`applyEditsToBytes`), to a stream (worker/src/metadata-stream.ts) or to a `Blob` (web/src/stripLocation.ts), so one
// set of rules serves a 5 KB photo and a 100 MB video.
//
// Rules, per format (docs/media.md "Photos and videos"):
//   JPEG  every APPn except JFIF, ICC and Adobe, and every comment, goes; the orientation stays.
//   PNG   eXIf, tEXt, zTXt, iTXt, tIME and anything after IEND go.
//   WebP  EXIF and XMP chunks go; their flags are cleared.
//   GIF   comments, XMP and unknown application blocks go; the animation block and the colour profile stay.
//   HEIC, HEIF, AVIF  the Exif and XMP items are zeroed in place and renamed "free"; no offset moves.
//   MP4, MOV  udta, meta, uuid and the other user-data boxes are renamed "free" and zeroed; a track of timed location
//         (GoPro, dashcams) cannot be removed in place, so the file is refused.
//   WebM, Matroska  Tags, Chapters and Attachments become Void elements.
// A file that should be cleaned and does not parse, and a picture or video type with no cleaner (TIFF and RAW, JPEG XL),
// is refused with a `CleanError`, never passed on with its location.

import { allowedExif } from "./exifAllow.ts";

export const ascii = (b: Uint8Array, at: number, n = 4) => String.fromCharCode(...b.subarray(at, at + n));
export const u16be = (b: Uint8Array, at: number) => (b[at]! << 8) | b[at + 1]!;
export const u32be = (b: Uint8Array, at: number) => ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;
export const u32le = (b: Uint8Array, at: number) => (b[at]! | (b[at + 1]! << 8) | (b[at + 2]! << 16) | (b[at + 3]! << 24)) >>> 0;
const latin1 = (b: Uint8Array) => new TextDecoder("latin1").decode(b);

export function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

// ---- Limits ----

/** Biggest moov (or top-level udta/meta/Tags) box read whole. A 10-minute 4K video's index is a few MB. */
export const MOOV_MAX = 32 * 1024 * 1024;
/** A JPEG's segments before the image data can't plausibly run longer than this (each is at most 64 KB). */
const JPEG_HEADER_MAX = 16 * 1024 * 1024;
/** Skips longer than this reopen the read at the new offset instead of reading through. */
const REOPEN_AT = 2 * 1024 * 1024;
/** A metadata block bigger than this is not read for a location: it counts as having one. */
const INSPECT_MAX = 1024 * 1024;

// ---- Errors ----

/**
 * Why a file was refused, for the words shown to the person:
 * - "location": the file shows a location that could not be removed.
 * - "format": the file is of a type Bloom has no cleaner for (`label` names it).
 * - "other": the file could not be checked: it does not parse, or it is too big to read safely.
 */
export type RefusalReason = "location" | "format" | "other";

/** The upload can't be made safe to store: a type with no cleaner, a file that does not parse, or a box too big to read. */
export class CleanError extends Error {
  /** What the file is, for the words shown to the person. */
  readonly subject: "picture" | "video";
  readonly reason: RefusalReason;
  /** The name of the format, when the reason is "format" (for example "AVI"). */
  readonly label: string | undefined;
  constructor(message: string, subject: "picture" | "video" = "video", reason: RefusalReason = "other", label?: string) {
    super(message);
    this.subject = subject;
    this.reason = reason;
    this.label = label;
  }
}
/** The file ended in the middle of a box or segment: it doesn't parse. */
class Truncated extends Error {}

// ---- The plan ----

/** Remove `remove` bytes at `at`, write `insert` there, then `zeros` zero bytes. */
export interface Edit {
  at: number;
  remove: number;
  insert?: Uint8Array;
  zeros?: number;
}

/** What the Worker learns about a video while cleaning it. */
export interface VideoInfo {
  /** Seconds, from the movie header. */
  duration?: number;
  /** H.265/HEVC video, which Chrome and Firefox on many devices can't play. */
  hevc: boolean;
}

export interface Plan {
  edits: Edit[];
  video?: VideoInfo;
  /** The file held a location (GPS in EXIF or XMP, QuickTime or MP4 location, a WebM location tag) before the plan. */
  location: boolean;
  /**
   * The allowlist EXIF block (`allowedExif`) the cleaned JPEG, PNG or WebP holds; for a HEIC or HEIF, the block its Exif item had
   * before the item was zeroed, with no Orientation. Checked values only (#379): the Worker builds the photo's record from it.
   */
  exif?: Uint8Array;
}

export type Open = (offset: number) => Promise<ReadableStream<Uint8Array>>;

/** An `Open` over bytes already in memory. */
export const openBytes =
  (b: Uint8Array): Open =>
  async (offset) =>
    new ReadableStream<Uint8Array>({
      start(c) {
        if (offset < b.length) c.enqueue(b.subarray(offset));
        c.close();
      },
    });

/** Size of the file once the edits are applied. */
export const cleanedSize = (size: number, edits: Edit[]) => size + edits.reduce((n, e) => n + (e.insert?.length ?? 0) + (e.zeros ?? 0) - e.remove, 0);

/** Sorted by offset; at the same offset, insertions first. Throws if two edits overlap. */
export function sortEdits(edits: Edit[]): Edit[] {
  const out = [...edits].sort((a, b) => a.at - b.at || a.remove - b.remove);
  for (let i = 1; i < out.length; i++) if (out[i]!.at < out[i - 1]!.at + out[i - 1]!.remove) throw new Error("overlapping edits");
  return out;
}

/** The bytes with the edits applied. The same array when there is nothing to do. */
export function applyEditsToBytes(b: Uint8Array, edits: Edit[]): Uint8Array {
  if (!edits.length) return b;
  const sorted = sortEdits(edits);
  const out = new Uint8Array(cleanedSize(b.length, sorted));
  let from = 0;
  let to = 0;
  for (const e of sorted) {
    out.set(b.subarray(from, e.at), to);
    to += e.at - from;
    if (e.insert) out.set(e.insert, to);
    to += (e.insert?.length ?? 0) + (e.zeros ?? 0);
    from = e.at + e.remove;
  }
  out.set(b.subarray(from), to);
  return out;
}

// ---- Reading a stored file in order ----

/** Reads a file in order, holding only the bytes asked for. */
export class Reader {
  pos = 0;
  private buf: Uint8Array = new Uint8Array(0);
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private open: Open;
  readonly size: number;
  constructor(open: Open, size: number) {
    this.open = open;
    this.size = size;
  }

  private async pull(): Promise<void> {
    if (this.pos + this.buf.length >= this.size) throw new Truncated("unexpected end of file");
    this.reader ??= (await this.open(this.pos + this.buf.length)).getReader();
    const { done, value } = await this.reader.read();
    if (done || !value) throw new Truncated("unexpected end of file");
    this.buf = this.buf.length ? concat([this.buf, value]) : value;
  }

  /** The next n bytes, exactly. */
  async take(n: number): Promise<Uint8Array> {
    if (n <= this.buf.length) {
      const out = this.buf.slice(0, n);
      this.buf = this.buf.subarray(n);
      this.pos += n;
      return out;
    }
    if (this.pos + n > this.size) throw new Truncated("unexpected end of file");
    // Collected in pieces, so a big box isn't copied over and over as it arrives.
    const parts: Uint8Array[] = [this.buf];
    let have = this.buf.length;
    this.buf = new Uint8Array(0);
    this.reader ??= (await this.open(this.pos + have)).getReader();
    while (have < n) {
      const { done, value } = await this.reader.read();
      if (done || !value) throw new Truncated("unexpected end of file");
      parts.push(value);
      have += value.length;
    }
    const all = concat(parts);
    this.buf = all.subarray(n);
    this.pos += n;
    return all.subarray(0, n);
  }

  /**
   * Reads the next n bytes into `target` at `at`, one chunk at a time: the box a caller builds is the only big buffer
   * (take() would hold the pieces, their joined copy and then the caller's copy of it).
   */
  async takeInto(target: Uint8Array, at: number, n: number): Promise<void> {
    if (this.pos + n > this.size) throw new Truncated("unexpected end of file");
    const fromBuffer = Math.min(n, this.buf.length);
    target.set(this.buf.subarray(0, fromBuffer), at);
    let done = fromBuffer;
    const buffered = this.buf.length;
    this.buf = this.buf.subarray(fromBuffer);
    if (done < n) {
      this.reader ??= (await this.open(this.pos + buffered)).getReader();
      while (done < n) {
        const { done: end, value } = await this.reader.read();
        if (end || !value) throw new Truncated("unexpected end of file");
        const use = Math.min(value.length, n - done);
        target.set(value.subarray(0, use), at + done);
        done += use;
        if (use < value.length) this.buf = value.subarray(use);
      }
    }
    this.pos += n;
  }

  /** The header already read, then the next n bytes, in one buffer. */
  async takeBox(header: Uint8Array, n: number): Promise<Uint8Array> {
    const out = new Uint8Array(header.length + n);
    out.set(header);
    await this.takeInto(out, header.length, n);
    return out;
  }

  /**
   * Moves to the next marker in JPEG scan data: an 0xFF followed by anything but a stuffed zero, a restart marker
   * (0xD0 to 0xD7) or another 0xFF. False when the file ends first. Leaves `pos` on the 0xFF.
   */
  async toMarker(): Promise<boolean> {
    for (;;) {
      const i = this.buf.indexOf(0xff);
      const at = i < 0 ? this.buf.length : i;
      this.buf = this.buf.subarray(at);
      this.pos += at;
      if (this.buf.length < 2) {
        if (this.pos + this.buf.length >= this.size) {
          this.pos += this.buf.length;
          this.buf = new Uint8Array(0);
          return false;
        }
        await this.pull();
        continue;
      }
      const m = this.buf[1]!;
      if (m === 0xff) this.buf = this.buf.subarray(1), (this.pos += 1);
      else if (m === 0 || (m >= 0xd0 && m <= 0xd7)) this.buf = this.buf.subarray(2), (this.pos += 2);
      else return true;
    }
  }

  async skip(n: number): Promise<void> {
    if (n <= this.buf.length) {
      this.buf = this.buf.subarray(n);
      this.pos += n;
      return;
    }
    n -= this.buf.length;
    this.pos += this.buf.length;
    this.buf = new Uint8Array(0);
    if (n > REOPEN_AT) {
      await this.reader?.cancel().catch(() => {});
      this.reader = null;
      this.pos += n;
      return;
    }
    while (n > 0) {
      await this.pull();
      const k = Math.min(n, this.buf.length);
      this.buf = this.buf.subarray(k);
      this.pos += k;
      n -= k;
    }
  }

  async close() {
    await this.reader?.cancel().catch(() => {});
    this.reader = null;
  }
}

/** n bytes at `at`, read with a stream of their own (the main read goes on where it was). */
async function readAt(open: Open, at: number, n: number): Promise<Uint8Array> {
  const stream = await open(at);
  const reader = stream.getReader();
  const parts: Uint8Array[] = [];
  let have = 0;
  try {
    while (have < n) {
      const { done, value } = await reader.read();
      if (done || !value) break;
      parts.push(value);
      have += value.length;
    }
  } finally {
    await reader.cancel().catch(() => {});
  }
  const all = concat(parts);
  if (all.length < n) throw new Truncated("unexpected end of file");
  return all.subarray(0, n);
}

/** Takes the next n bytes and tests them; a block too big to read counts as a hit. */
async function look(r: Reader, n: number, test: (b: Uint8Array) => boolean): Promise<boolean> {
  if (n > INSPECT_MAX) {
    await r.skip(n);
    return true;
  }
  return test(await r.take(n));
}

// ---- Location in metadata ----

/** XMP (or any text) with a GPS field. */
const GPS_TEXT = /exif:GPS|GPS(Latitude|Longitude|Altitude|Position)/;
export const textHasGps = (b: Uint8Array) => GPS_TEXT.test(latin1(b));

/**
 * An EXIF block (a TIFF header, or "Exif\0\0" then one) whose GPS directory holds anything besides its version. Except a
 * directory that is only zeros: a latitude and a longitude, every number in them (and in the destination ones, if present) zero.
 * A camera with no fix writes that (a Leica M11-P). It is cut like any GPS directory, but it is not a leak.
 */
export function exifHasGps(b: Uint8Array, from = 0): boolean {
  let t = from;
  if (ascii(b, t, 6) === "Exif\0\0") t += 6;
  const order = ascii(b, t, 2);
  if (order !== "II" && order !== "MM") return false;
  const le = order === "II";
  const r16 = (at: number) => (t + at + 2 <= b.length && at >= 0 ? (le ? b[t + at]! | (b[t + at + 1]! << 8) : u16be(b, t + at)) : -1);
  const r32 = (at: number) => (t + at + 4 <= b.length && at >= 0 ? (le ? u32le(b, t + at) : u32be(b, t + at)) : -1);
  const ifd = r32(4);
  const count = r16(ifd);
  for (let i = 0; i < count; i++) {
    const e = ifd + 2 + i * 12;
    if (r16(e) !== 0x8825) continue;
    // GPSInfo: a pointer to the GPS directory. One that points outside the block counts as a location.
    const gps = r32(e + 8);
    const n = r16(gps);
    if (n < 0) return true;
    let any = false;
    const coordinates = new Set<number>();
    for (let j = 0; j < n; j++) {
      const at = gps + 2 + j * 12;
      const tag = r16(at);
      if (tag <= 0) continue;
      any = true;
      if (tag === 2 || tag === 4 || tag === 0x14 || tag === 0x16) {
        // A RATIONAL list that is in the block and has nothing but zero numerators; else it is a place.
        const cnt = r32(at + 4);
        const off = r32(at + 8);
        if (r16(at + 2) !== 5 || cnt < 1 || cnt > 3 || off < 0 || t + off + cnt * 8 > b.length) return true;
        for (let k = 0; k < cnt; k++) if (r32(off + k * 8) !== 0) return true;
        coordinates.add(tag);
      }
    }
    return any && !(coordinates.has(2) && coordinates.has(4));
  }
  return false;
}

/** The EXIF orientation (1–8) in an Exif block, with or without the "Exif\0\0" prefix of a JPEG's APP1, if it has one. */
export function exifOrientation(seg: Uint8Array): number | undefined {
  const t = ascii(seg, 0, 6) === "Exif\0\0" ? 6 : 0;
  if (ascii(seg, t, 2) !== "II" && ascii(seg, t, 2) !== "MM") return;
  const le = ascii(seg, t, 2) === "II";
  const r16 = (at: number) => (le ? seg[t + at]! | (seg[t + at + 1]! << 8) : u16be(seg, t + at));
  const r32 = (at: number) => (le ? u32le(seg, t + at) : u32be(seg, t + at));
  const ifd = r32(4);
  if (t + ifd + 2 > seg.length) return;
  const count = r16(ifd);
  for (let i = 0; i < count; i++) {
    const e = ifd + 2 + i * 12;
    if (t + e + 12 > seg.length) return;
    if (r16(e) === 0x0112) {
      const v = r16(e + 8);
      return v >= 1 && v <= 8 ? v : undefined;
    }
  }
}

/** A TIFF block (what PNG's eXIf and WebP's EXIF chunks hold) with nothing but the orientation: 26 bytes. */
export function orientationTiff(o: number): Uint8Array {
  // Big-endian header, one IFD with one SHORT entry (0x0112), no next IFD.
  return new Uint8Array([0x4d, 0x4d, 0, 0x2a, 0, 0, 0, 8, 0, 1, 0x01, 0x12, 0, 3, 0, 0, 0, 1, 0, o, 0, 0, 0, 0, 0, 0]);
}

const sameBytes = (a: Uint8Array, b: Uint8Array) => a.length === b.length && a.every((x, i) => x === b[i]);

const CRC_TABLE = Uint32Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});
export function crc32(b: Uint8Array): number {
  let c = 0xffffffff;
  for (const x of b) c = CRC_TABLE[(c ^ x) & 255]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** A PNG eXIf chunk with this block. */
function exifPngChunk(tiff: Uint8Array): Uint8Array {
  const body = concat([new Uint8Array([0x65, 0x58, 0x49, 0x66]), tiff]); // "eXIf" + data
  const out = new Uint8Array(12 + tiff.length);
  const v = new DataView(out.buffer);
  v.setUint32(0, tiff.length);
  out.set(body, 4);
  v.setUint32(8 + tiff.length, crc32(body));
  return out;
}

/** An APP1 segment with this block. */
export function exifSegment(tiff: Uint8Array): Uint8Array {
  const len = 2 + 6 + tiff.length;
  return concat([new Uint8Array([0xff, 0xe1, len >> 8, len & 0xff, 0x45, 0x78, 0x69, 0x66, 0, 0]), tiff]);
}

/**
 * A JPEG with `tiff` (an allowlist block from `allowedExif`) as its one Exif segment, placed after JFIF as the cleaner places it
 * (#380). Any Exif segment the JPEG had before the first scan is dropped, so the copy holds that block and no other. Undefined
 * when the bytes are not a JPEG whose segments can be walked up to the first scan.
 */
export function withExifBlock(jpeg: Uint8Array, tiff: Uint8Array): Uint8Array | undefined {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return;
  const parts: Uint8Array[] = [jpeg.subarray(0, 2)];
  let at = 2;
  let placed = false;
  const place = () => {
    if (!placed) parts.push(exifSegment(tiff));
    placed = true;
  };
  while (at + 4 <= jpeg.length) {
    if (jpeg[at] !== 0xff) return;
    const marker = jpeg[at + 1]!;
    if (marker === 0xff) {
      at++; // a fill byte
      continue;
    }
    if (marker === 0xda || marker === 0xd9) break; // the first scan: everything from here stays
    const len = (jpeg[at + 2]! << 8) | jpeg[at + 3]!;
    if (len < 2 || at + 2 + len > jpeg.length) return;
    const seg = jpeg.subarray(at, at + 2 + len);
    const isExif = marker === 0xe1 && ascii(seg, 4, 6) === "Exif\0\0";
    if (!isExif) {
      // The Exif segment goes right after JFIF when that comes first, else before the first other segment.
      if (marker !== 0xe0 || parts.length > 1) place();
      parts.push(seg);
    }
    at += 2 + len;
  }
  place();
  parts.push(jpeg.subarray(at));
  return concat(parts);
}

/** An APP1 segment holding nothing but the orientation. */
export function orientationSegment(o: number): Uint8Array {
  // "Exif\0\0" and then the TIFF block.
  const payload = [0x45, 0x78, 0x69, 0x66, 0, 0, ...orientationTiff(o)];
  const len = payload.length + 2;
  return new Uint8Array([0xff, 0xe1, len >> 8, len & 0xff, ...payload]);
}

// ---- JPEG ----

// APP0 (JFIF), APP2 (ICC colour profile) and APP14 (Adobe colour transform) change how the picture looks: kept.
// Everything else before the image data (APP1 EXIF/XMP, APP13 IPTC, the other APPn, comments) goes.
export const JPEG_KEEP = new Set([0xe0, 0xe2, 0xee]);

/**
 * A JFIF APP0 segment with no thumbnail, written again from the fields of a JFIF APP0 body; undefined when the body is not JFIF.
 * No source byte is copied unchecked (#384): the version is the source's when its major is 1 and its minor 0 to 2, else 1.01; the
 * units and both densities are the source's when the units are 0 to 2 and both densities 1 to 65535, else units 0 and density 1:1.
 */
function jfifSegment(body: Uint8Array): Uint8Array | undefined {
  if (body.length < 14 || ascii(body, 0, 5) !== "JFIF\0") return;
  const versionOk = body[5] === 1 && body[6]! <= 2;
  const densityOk = body[7]! <= 2 && u16be(body, 8) >= 1 && u16be(body, 10) >= 1;
  // Apple writes the four bytes "AMPF" after the 0 by 0 thumbnail size, and its decoders read them. Those four bytes, and only those, stay:
  // they are written from a constant here, not copied. Any other trailing byte goes.
  const ampf = body.length === 18 && body[12] === 0 && body[13] === 0 && ascii(body, 14, 4) === "AMPF";
  return concat([
    new Uint8Array([0xff, 0xe0, 0, ampf ? 20 : 16]),
    new Uint8Array([0x4a, 0x46, 0x49, 0x46, 0, versionOk ? body[5]! : 1, versionOk ? body[6]! : 1]),
    densityOk ? body.subarray(7, 12) : new Uint8Array([0, 0, 1, 0, 1]),
    new Uint8Array([0, 0]),
    ampf ? new Uint8Array([0x41, 0x4d, 0x50, 0x46]) : new Uint8Array(0),
  ]);
}

/**
 * An Adobe APP14 segment, from an Adobe APP14 body of at least 12 bytes; undefined when the body is not that. The name, the
 * version (100) and both flags words (0) are constants here, never the file's. The transform is the file's only when it is
 * 0 to 2 (RGB or CMYK, YCbCr, YCCK), else 0.
 */
function adobeSegment(body: Uint8Array): Uint8Array | undefined {
  if (body.length < 12 || ascii(body, 0, 5) !== "Adobe") return;
  const transform = body[11]! <= 2 ? body[11]! : 0;
  return new Uint8Array([0xff, 0xee, 0, 14, 0x41, 0x64, 0x6f, 0x62, 0x65, 0, 100, 0, 0, 0, 0, transform]);
}

async function planJpeg(r: Reader): Promise<Plan | null> {
  const soi = await r.take(2);
  if (soi[0] !== 0xff || soi[1] !== 0xd8) return null;
  const edits: Edit[] = [];
  let location = false;
  let exifBuilt: Uint8Array | undefined; // the little-endian block that replaces the picture's EXIF
  let seenExif = false;
  let exifKept = false; // the picture's own EXIF block is that block already
  let firstKept: { marker: number; end: number } | undefined;
  let scanned = false;
  for (;;) {
    if (!scanned && r.pos > JPEG_HEADER_MAX) return null;
    if (scanned) {
      // After a scan: skip its data to the next marker. A file that ends here has no EOI and nothing after it.
      if (!(await r.toMarker())) break;
    } else if (r.pos + 4 > r.size) return null;
    else {
      // Bytes between two segments that are not a marker (zero padding some encoders write): cut, up to the next marker.
      let b = (await r.take(1))[0]!;
      if (b !== 0xff) {
        const pad = r.pos - 1;
        while (b !== 0xff) {
          if (r.pos > JPEG_HEADER_MAX || r.pos + 4 > r.size) return null;
          b = (await r.take(1))[0]!;
        }
        edits.push({ at: pad, remove: r.pos - 1 - pad });
      }
    }
    if (scanned) await r.skip(1);
    let marker = (await r.take(1))[0]!;
    while (marker === 0xff) marker = (await r.take(1))[0]!; // fill bytes
    const start = r.pos - 2;
    if (marker === 0xd9) {
      // The end of the picture. Whatever follows is not the picture: a multi-picture JPEG's other pictures (each with
      // its own EXIF), a motion photo's video, an old thumbnail. All of it goes.
      const rest = r.size - r.pos;
      if (rest > 0) {
        const at = r.pos;
        location = (await look(r, rest, trailingHasLocation)) || location;
        edits.push({ at, remove: rest });
      }
      break;
    }
    if (marker === 0xda && !scanned) {
      // First start of scan: the orientation goes right after JFIF's APP0 when there is one (it has to come first),
      // else right after the start marker.
      if (exifBuilt && !exifKept) edits.push({ at: firstKept?.marker === 0xe0 ? firstKept.end : 2, remove: 0, insert: exifSegment(exifBuilt) });
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue; // no length
    const len = u16be(await r.take(2), 0);
    if (len < 2 || start + 2 + len > r.size) return null;
    if (marker === 0xda) scanned = true;
    // A metadata segment, before the first scan or between scans. APP0, APP2 and APP14 stay by their content, not their number:
    // JFIF (written again with no thumbnail), an ICC profile and Adobe's marker. JFXX, the multi-picture index, a gain-map stub,
    // FPXR and any other APP0, APP2 or APP14 go.
    let isMeta = (marker >= 0xe0 && marker <= 0xef && !JPEG_KEEP.has(marker)) || marker === 0xfe;
    let body: Uint8Array | undefined;
    let rewritten: Uint8Array | undefined; // a JFIF or Adobe segment, written again from its fields
    if (!isMeta && JPEG_KEEP.has(marker)) {
      body = await r.take(len - 2);
      if (marker === 0xe0 || marker === 0xee) {
        rewritten = marker === 0xe0 ? jfifSegment(body) : adobeSegment(body);
        isMeta = !rewritten;
      } else isMeta = ascii(body, 0, 12) !== "ICC_PROFILE\0";
    }
    if (isMeta) {
      body ??= await r.take(len - 2);
      let keep = false;
      if (marker === 0xe1) {
        if (ascii(body, 0, 6) === "Exif\0\0") {
          location ||= exifHasGps(body);
          if (!seenExif) {
            // The first Exif block is rebuilt from its checked values (exifAllow.ts); a block that is already that stays as it is.
            seenExif = true;
            exifBuilt = allowedExif(body);
            if (exifBuilt && !scanned && sameBytes(body.subarray(6), exifBuilt)) keep = exifKept = true;
          }
        } else location ||= textHasGps(body);
      }
      if (keep) firstKept ??= { marker, end: r.pos };
      else edits.push({ at: start, remove: 2 + len });
    } else {
      if (!body) await r.skip(len - 2);
      if (rewritten && !(rewritten.length === len + 2 && sameBytes(rewritten.subarray(4), body!))) edits.push({ at: start, remove: 2 + len, insert: rewritten });
      if (!scanned) firstKept ??= { marker, end: r.pos };
    }
  }
  return { edits, location, exif: exifBuilt };
}

/** Bytes after a JPEG's EOI: do they hold a location? An EXIF block with GPS, XMP or MP4 location boxes. */
function trailingHasLocation(b: Uint8Array): boolean {
  const s = latin1(b);
  if (textHasGps(b) || MOV_LOCATION.test(s)) return true;
  for (let i = s.indexOf("Exif\0\0"); i >= 0; i = s.indexOf("Exif\0\0", i + 1)) if (exifHasGps(b, i)) return true;
  return false;
}

// ---- PNG ----

// The PNG chunks that stay: the critical ones (an upper-case first letter: IHDR, PLTE, IDAT, IEND, and any other the format
// adds) and the ancillary ones that change how the picture looks or plays. Everything else goes: text, time, EXIF, C2PA
// (caBX), and any chunk a vendor made up. A list of what stays, not of what goes: a new kind of chunk is dropped until it is
// added here.
export const PNG_KEEP = new Set(["tRNS", "cHRM", "gAMA", "iCCP", "sBIT", "sRGB", "cICP", "mDCV", "cLLI", "bKGD", "hIST", "pHYs", "sPLT", "acTL", "fcTL", "fdAT", "oFFs", "pCAL", "sCAL", "gIFg", "gIFx", "sTER"]);
const pngKeeps = (type: string) => PNG_KEEP.has(type) || (type.charCodeAt(0) >= 0x41 && type.charCodeAt(0) <= 0x5a);

async function planPng(r: Reader): Promise<Plan | null> {
  const sig = await r.take(8);
  if (u32be(sig, 0) !== 0x89504e47 || u32be(sig, 4) !== 0x0d0a1a0a) return null;
  const edits: Edit[] = [];
  let location = false;
  let sawEnd = false;
  let pngExif = 0;
  let exif: Uint8Array | undefined;
  while (r.pos + 12 <= r.size) {
    const start = r.pos;
    const h = await r.take(8);
    const len = u32be(h, 0);
    const type = ascii(h, 4);
    if (start + 12 + len > r.size) return null;
    if (!pngKeeps(type)) {
      edits.push({ at: start, remove: 12 + len });
      if (type === "eXIf" && len <= INSPECT_MAX) {
        // The camera, the date taken, the shot settings and the orientation stay, rebuilt in a new chunk (exifAllow.ts).
        const data = await r.take(len);
        const crc = await r.take(4);
        location = exifHasGps(data) || location;
        const built = pngExif++ === 0 ? allowedExif(data) : undefined;
        if (built) {
          exif = built;
          // Already that chunk, with a right CRC: it stays. Else it is written again, with a CRC worked out here.
          const chunk = exifPngChunk(built);
          if (sameBytes(data, built) && sameBytes(crc, chunk.subarray(chunk.length - 4))) edits.pop();
          else edits[edits.length - 1] = { at: start, remove: 12 + len, insert: chunk };
        }
      } else if (type === "eXIf") {
        location = true;
        await r.skip(len + 4);
      } else {
        location = (await look(r, len, textHasGps)) || location;
        await r.skip(4);
      }
    } else await r.skip(len + 4);
    if (type === "IEND") {
      sawEnd = true;
      break;
    }
  }
  if (!sawEnd) return null;
  // Whatever trails the image goes too.
  if (r.pos < r.size) edits.push({ at: r.pos, remove: r.size - r.pos });
  return { edits, location, exif };
}

// ---- WebP ----

// The WebP chunks that stay: the picture (VP8, VP8L, ALPH), the animation (ANIM, ANMF), the flags (VP8X) and the colour
// profile (ICCP). EXIF, XMP, C2PA and anything unknown go.
const WEBP_KEEP = new Set(["VP8 ", "VP8L", "VP8X", "ALPH", "ANIM", "ANMF", "ICCP"]);

async function planWebp(r: Reader): Promise<Plan | null> {
  const head = await r.take(12);
  if (ascii(head, 0) !== "RIFF" || ascii(head, 8) !== "WEBP") return null;
  const edits: Edit[] = [];
  let location = false;
  let vp8x: { at: number; chunk: Uint8Array } | undefined;
  let exifKept = false; // an EXIF chunk, rebuilt, stays in the file
  let exif: Uint8Array | undefined;
  while (r.pos + 8 <= r.size) {
    const start = r.pos;
    const h = await r.take(8);
    const type = ascii(h, 0);
    const len = u32le(h, 4);
    const end = Math.min(r.size, start + 8 + len + (len & 1));
    if (!WEBP_KEEP.has(type)) {
      const n = end - start - 8;
      if (type === "EXIF" && len <= INSPECT_MAX && end - start === 8 + len + (len & 1)) {
        // The camera, the date taken, the shot settings and the orientation stay, rebuilt in a new chunk (needs the VP8X header to say so).
        const data = await r.take(n);
        location = exifHasGps(data) || location;
        const built = vp8x && !exifKept ? allowedExif(data.subarray(0, len)) : undefined;
        if (built) {
          exifKept = true;
          exif = built;
          if (!(len === built.length && sameBytes(data.subarray(0, len), built))) {
            const size = new Uint8Array([built.length & 255, (built.length >> 8) & 255, 0, 0]);
            edits.push({ at: start, remove: end - start, insert: concat([h.slice(0, 4), size, built]) });
          }
        } else edits.push({ at: start, remove: end - start });
      } else {
        edits.push({ at: start, remove: end - start });
        location = (await look(r, n, (b) => (type === "EXIF" ? exifHasGps(b) : textHasGps(b)))) || location;
      }
    } else if (type === "VP8X" && end - start <= 64) {
      vp8x = { at: start, chunk: concat([h, await r.take(end - start - 8)]) };
    } else {
      await r.skip(end - start - 8);
    }
  }
  if (vp8x) {
    // The flags: "has XMP" (0x04) is cleared; "has EXIF" (0x08) stays only when the orientation chunk does.
    const chunk = vp8x.chunk.slice();
    chunk[8] = chunk[8]! & (exifKept ? ~0x04 : ~0x0c);
    if (chunk[8] !== vp8x.chunk[8]) edits.push({ at: vp8x.at, remove: chunk.length, insert: chunk });
  }
  if (!edits.length) return { edits, location, exif };
  // The RIFF header carries the file's length.
  const out = cleanedSize(r.size, edits);
  const size = out - 8;
  edits.unshift({ at: 4, remove: 4, insert: new Uint8Array([size & 0xff, (size >> 8) & 0xff, (size >> 16) & 0xff, (size >>> 24) & 0xff]) });
  return { edits, location, exif };
}

// ---- GIF ----

// Application blocks that change how the picture plays or looks stay: looping, and the colour profile.
const GIF_KEEP_APPS = new Set(["NETSCAPE2.0", "ANIMEXTS1.0", "ICCRGBG1012"]);

async function planGif(r: Reader): Promise<Plan | null> {
  const head = await r.take(13);
  if (ascii(head, 0, 3) !== "GIF") return null;
  const edits: Edit[] = [];
  let location = false;
  if (head[10]! & 0x80) await r.skip(3 * 2 ** ((head[10]! & 7) + 1));
  /** The sub-blocks from here to the zero that ends them: their bytes, or null when they are too many to read. */
  const subBlocks = async (keep: boolean): Promise<Uint8Array | null> => {
    const parts: Uint8Array[] = [];
    let total = 0;
    for (;;) {
      const n = (await r.take(1))[0]!;
      if (n === 0) return parts.length ? concat(parts) : new Uint8Array(0);
      if (keep || total + n > INSPECT_MAX) {
        await r.skip(n);
        total += n;
      } else {
        parts.push(await r.take(n));
        total += n;
      }
    }
  };
  const trailer = new Uint8Array([0x3b]);
  for (;;) {
    const start = r.pos;
    // A file that ends between two blocks, with no trailer: the trailer is added.
    if (start >= r.size) {
      edits.push({ at: start, remove: 0, insert: trailer });
      return { edits, location };
    }
    const kind = (await r.take(1))[0]!;
    if (kind === 0x3b) {
      // Trailer: whatever follows goes.
      if (r.pos < r.size) edits.push({ at: r.pos, remove: r.size - r.pos });
      return { edits, location };
    }
    if (kind === 0x21) {
      const label = (await r.take(1))[0]!;
      if (label === 0xfe) {
        const text = await subBlocks(false);
        edits.push({ at: start, remove: r.pos - start });
        location = (text ? textHasGps(text) : true) || location;
      } else if (label === 0xff) {
        const size = (await r.take(1))[0]!;
        const app = latin1(await r.take(size));
        if (GIF_KEEP_APPS.has(app)) await subBlocks(true);
        else {
          const text = await subBlocks(false);
          edits.push({ at: start, remove: r.pos - start });
          location = (text ? textHasGps(text) : true) || location;
        }
      } else {
        // Graphic control and plain text: sub-blocks follow the label; kept.
        await subBlocks(true);
      }
    } else if (kind === 0x2c) {
      const d = await r.take(9);
      if (d[8]! & 0x80) await r.skip(3 * 2 ** ((d[8]! & 7) + 1));
      await r.skip(1); // LZW minimum code size
      await subBlocks(true);
    } else {
      // Not a block: what follows cannot be read as a picture, and a player stops here too. It is cut, and the trailer added.
      location = (await look(r, r.size - r.pos, textHasGps)) || location;
      edits.push({ at: start, remove: r.size - start, insert: trailer });
      return { edits, location };
    }
  }
}

// ---- MP4, MOV, HEIC, HEIF and AVIF (ISO base media) ----

/**
 * Boxes under the file, moov and each trak that carry user data or a location, whatever their name: QuickTime and
 * Android location, device make and model, dates, XMP, 3GPP location. Renamed "free" and zeroed.
 */
export const MOV_META = new Set(["udta", "meta", "uuid", "XMP_", "xml ", "gps ", "GPS ", "loci", "\xa9xyz"]);

/** Text that marks a box as holding a location. */
const MOV_LOCATION = /\xa9xyz|loci|[Ll]ocation|ISO6709|GPS|gps |[Ll]atitude|[Ll]ongitude/;
/** A track of timed location (GoPro `gpmd`, camera motion `camm`, dashcam `gps `), or timed metadata whose keys name a location. */
const LOCATION_TRACK = /gpmd|camm|GPS |gps /;

interface Box {
  type: string;
  start: number;
  body: number;
  end: number;
}

/**
 * The boxes in b[from, to) (64-bit sizes and "to the end" included). A box whose size is under its header or runs past
 * the end of its parent does not parse: it throws, so nothing after it is trusted. Fewer than 8 bytes at the end (the
 * zero terminator some QuickTime user data ends with) are padding.
 */
function boxList(b: Uint8Array, from: number, to: number, last = false): Box[] {
  const out: Box[] = [];
  let at = from;
  while (at + 8 <= to) {
    let size = u32be(b, at);
    let body = at + 8;
    if (size === 1) {
      if (at + 16 > to) throw new Truncated("a box header runs past its parent");
      size = u32be(b, at + 8) * 2 ** 32 + u32be(b, at + 12);
      body = at + 16;
    } else if (size === 0) {
      // "To the end" is allowed for the last box of the file only (ISO 14496-12): below the top level it is refused.
      if (!last) throw new Truncated("a box of size 0 below the top level");
      size = to - at;
    }
    if (size < body - at || at + size > to) throw new Truncated("a box runs past its parent or is smaller than its header");
    out.push({ type: ascii(b, at + 4), start: at, body, end: at + size });
    at += size;
  }
  return out;
}

/** Whether the four ASCII characters `s` appear in b[from, to). */
function has(b: Uint8Array, from: number, to: number, s: string) {
  const [c0, c1, c2, c3] = [0, 1, 2, 3].map((i) => s.charCodeAt(i));
  for (let i = from; i + 4 <= to; i++) if (b[i] === c0 && b[i + 1] === c1 && b[i + 2] === c2 && b[i + 3] === c3) return true;
  return false;
}

/** The children of moov and moof that stay. Every other child is blanked, as at the top level. */
const MOOV_KEEP = new Set(["mvhd", "trak", "mvex", "iods", "pssh", "free", "skip", "wide"]);
const MOOF_KEEP = new Set(["mfhd", "traf", "pssh", "free", "skip", "wide"]);

/** Boxes whose children are walked to reach user data: the movie, a track and what is inside it, a fragment. */
const ISO_CONTAINERS = new Set(["moov", "trak", "mdia", "minf", "stbl", "edts", "dinf", "mvex", "moof", "traf"]);

/**
 * The moov or moof box, cleaned in place: every user-data box in it, at any depth (under trak, mdia, minf, stbl, edts,
 * dinf, mvex, traf), is renamed "free" and zeroed (same size, so no offset moves and nothing is re-encoded). Players skip
 * free boxes. Throws CleanError for a track of timed location, which can't be removed without moving the file's data.
 */
export function cleanMoov(b: Uint8Array): { info: VideoInfo; changed: boolean; location: boolean } {
  const info: VideoInfo = { hevc: false };
  let changed = false;
  let location = false;
  const rename = (x: Box) => {
    changed = true;
    if (MOV_LOCATION.test(latin1(b.subarray(x.body, x.end)))) location = true;
    b.set([0x66, 0x72, 0x65, 0x65], x.start + 4);
    b.fill(0, x.body, x.end);
  };
  // The box itself may be the last one in the file, with size 0.
  const root = boxList(b, 0, b.length, true)[0];
  if (!root || (root.type !== "moov" && root.type !== "moof")) throw new Truncated("no moov");
  const keep = root.type === "moov" ? MOOV_KEEP : MOOF_KEEP;
  const walk = (from: number, to: number) => {
    for (const x of boxList(b, from, to)) {
      if (from === root.body && x.type === "cmov") throw new CleanError("this video's movie box is compressed, so its hidden details can't be checked", "video");
      if (MOV_META.has(x.type) || (from === root.body && !keep.has(x.type))) rename(x);
      else if (x.type === "mvhd" && root.type === "moov") {
        const v = b[x.body]!;
        const scale = v === 1 ? u32be(b, x.body + 20) : u32be(b, x.body + 12);
        const dur = v === 1 ? u32be(b, x.body + 24) * 2 ** 32 + u32be(b, x.body + 28) : u32be(b, x.body + 16);
        if (scale > 0) info.duration = dur / scale;
      } else if (ISO_CONTAINERS.has(x.type)) {
        walk(x.body, x.end);
        if (x.type === "trak") {
          const text = latin1(b.subarray(x.body, x.end));
          if (LOCATION_TRACK.test(text) || (text.includes("mebx") && /[Ll]ocation|GPS|ISO6709/.test(text))) {
            throw new CleanError("this video has a track of timed location, which can't be removed", "video");
          }
        }
      }
    }
  };
  walk(root.body, root.end);
  // Sample entries name the codec; HEVC is hvc1 or hev1.
  info.hevc = has(b, root.body, root.end, "hvc1") || has(b, root.body, root.end, "hev1");
  return { info, changed, location };
}

/** A box header with its type changed to "free". */
const freeHeader = (h: Uint8Array) => {
  const out = h.slice();
  out.set([0x66, 0x72, 0x65, 0x65], 4);
  return out;
};

const cstr = (b: Uint8Array, at: number) => {
  let e = at;
  while (e < b.length && b[e] !== 0) e++;
  return { text: latin1(b.subarray(at, e)), next: e + 1 };
};
const rN = (b: Uint8Array, at: number, n: number) => {
  let v = 0;
  for (let i = 0; i < n; i++) v = v * 256 + b[at + i]!;
  return v;
};

/**
 * The item types of a picture that stay (the coded images and what is built from them). Every other item (Exif, XMP, a
 * vendor's data, a depth map's metadata) is renamed "free" and zeroed.
 */
const HEIF_IMAGE_ITEMS = new Set(["hvc1", "hev1", "av01", "avc1", "avc3", "vvc1", "evc1", "jpeg", "mjpg", "j2k1", "jxl ", "grid", "iovl", "iden", "tmap", "unci", "uncv", "mski"]);
/** The boxes inside a HEIF meta box that stay. Every other box in it is blanked. */
const HEIF_META_KEEP = new Set(["hdlr", "pitm", "iinf", "iloc", "iprp", "iref", "idat", "dinf", "ipro", "grpl", "free", "skip"]);

/** Metadata items of an image (HEIC, HEIF, AVIF) that are not coded pictures, found in a meta box. */
interface MetaItem {
  type: string;
  /** Where in the file the item's type name sits. */
  typeAt: number;
  exif: boolean;
  ranges: { at: number; length: number; inMeta?: boolean }[];
}

/**
 * The Exif and XMP items of a `meta` box (whole, in `box`, starting at file offset `start`): their data ranges and the
 * place of their type names. Items are renamed "free" and their data zeroed, so every other offset stays.
 */
function heifItems(box: Uint8Array, start: number, hlen: number): MetaItem[] {
  const kids = boxList(box, hlen + 4, box.length);
  const iinf = kids.find((k) => k.type === "iinf");
  const iloc = kids.find((k) => k.type === "iloc");
  const idat = kids.find((k) => k.type === "idat");
  if (!iinf || !iloc) return [];
  const targets = new Map<number, MetaItem>();
  const v = box[iinf.body]!;
  for (const infe of boxList(box, iinf.body + 4 + (v === 0 ? 2 : 4), iinf.end)) {
    if (infe.type !== "infe") continue;
    const ver = box[infe.body]!;
    // Before version 2 an entry has no item type, so what the item is cannot be told: nothing is trusted.
    if (ver < 2) throw new CleanError("an item of this picture has no type Bloom can read", "picture");
    const id = ver === 2 ? u16be(box, infe.body + 4) : u32be(box, infe.body + 4);
    const typeAt = infe.body + (ver === 2 ? 8 : 10);
    const type = ascii(box, typeAt);
    if (HEIF_IMAGE_ITEMS.has(type) || type === "free") continue;
    targets.set(id, { type, typeAt: start + typeAt, exif: type === "Exif", ranges: [] });
  }
  if (!targets.size) return [];
  // iloc: where each item's data is.
  const ver = box[iloc.body]!;
  let at = iloc.body + 4;
  const offsetSize = box[at]! >> 4;
  const lengthSize = box[at]! & 15;
  const baseSize = box[at + 1]! >> 4;
  const indexSize = ver === 1 || ver === 2 ? box[at + 1]! & 15 : 0;
  at += 2;
  const count = ver < 2 ? u16be(box, at) : u32be(box, at);
  at += ver < 2 ? 2 : 4;
  for (let i = 0; i < count; i++) {
    // The count comes from the file: a hostile one must not keep the loop going past the box.
    if (at + 8 > box.length) throw new Truncated("iloc ends early");
    const id = ver < 2 ? u16be(box, at) : u32be(box, at);
    at += ver < 2 ? 2 : 4;
    let method = 0;
    if (ver === 1 || ver === 2) {
      method = u16be(box, at) & 15;
      at += 2;
    }
    at += 2; // data reference index
    const base = rN(box, at, baseSize);
    at += baseSize;
    const extents = u16be(box, at);
    at += 2;
    const item = targets.get(id);
    for (let j = 0; j < extents; j++) {
      if (at + indexSize + offsetSize + lengthSize > box.length) throw new Truncated("iloc ends early");
      at += indexSize;
      const off = rN(box, at, offsetSize);
      at += offsetSize;
      const length = rN(box, at, lengthSize);
      at += lengthSize;
      if (!item) continue;
      if (method === 2 || (lengthSize === 0 && length === 0)) throw new CleanError("an Exif or XMP item is stored in a way that can't be cleaned in place", "picture");
      if (method === 1) {
        if (!idat) throw new Truncated("idat missing");
        item.ranges.push({ at: start + idat.body + base + off, length, inMeta: true });
      } else item.ranges.push({ at: base + off, length });
    }
  }
  return [...targets.values()];
}

/** The EXIF block of a HEIF Exif item: a 4-byte offset to the TIFF header, then the data. */
const heifExifHasGps = (b: Uint8Array) => (b.length >= 4 ? exifHasGps(b, Math.min(b.length, 4 + u32be(b, 0))) : false);

async function planHeifMeta(box: Uint8Array, start: number, hlen: number, size: number, open: Open): Promise<{ edits: Edit[]; location: boolean; exif?: Uint8Array }> {
  const items = heifItems(box, start, hlen);
  const edits: Edit[] = [];
  let location = false;
  // Boxes inside meta that are not on the list are blanked in place (same size).
  for (const k of boxList(box, hlen + 4, box.length)) {
    if (HEIF_META_KEEP.has(k.type)) continue;
    if (k.end - k.body <= INSPECT_MAX) location = MOV_LOCATION.test(latin1(box.subarray(k.body, k.end))) || textHasGps(box.subarray(k.body, k.end)) || location;
    else location = true;
    edits.push({ at: start + k.start, remove: k.end - k.start, insert: freeHeader(box.subarray(k.start, k.body)), zeros: k.end - k.body });
  }
  const ranges: { at: number; length: number }[] = [];
  let exif: Uint8Array | undefined;
  for (const item of items) {
    edits.push({ at: item.typeAt, remove: 4, insert: new Uint8Array([0x66, 0x72, 0x65, 0x65]) });
    const parts: Uint8Array[] = [];
    for (const rg of item.ranges) {
      if (rg.at + rg.length > size || rg.at < 0) throw new CleanError("an Exif or XMP item lies outside the file", "picture");
      if (rg.length > MOOV_MAX) throw new CleanError("an Exif or XMP item is too big to clean", "picture");
      ranges.push(rg);
      if (rg.length > INSPECT_MAX) location = true;
      else parts.push(rg.inMeta ? box.subarray(rg.at - start, rg.at - start + rg.length) : await readAt(open, rg.at, rg.length));
    }
    const data = concat(parts);
    if (data.length) location = (item.exif ? heifExifHasGps(data) : textHasGps(data)) || location;
    // The first Exif item's allowlist block, read before the item is zeroed (#380): a server that converts the picture to JPEG puts it
    // into the JPEG. No Orientation: the HEIC's own rotation is applied when it is decoded.
    if (item.exif && !exif && data.length >= 4 && 4 + u32be(data, 0) < data.length) exif = allowedExif(data.subarray(4 + u32be(data, 0)), { orientation: false });
  }
  // Data is zeroed once, even when two items share a range.
  ranges.sort((a, b) => a.at - b.at);
  let end = -1;
  for (const rg of ranges) {
    const from = Math.max(rg.at, end);
    const to = rg.at + rg.length;
    if (to > from) {
      edits.push({ at: from, remove: to - from, zeros: to - from });
      end = to;
    }
  }
  return { edits, location, exif };
}

/** Top-level boxes that may be cut off by the end of the file: a recording that stopped. Their content is media. */
const ISO_INERT = new Set(["mdat", "free", "skip", "wide", "junk"]);

/**
 * The top-level boxes that stay (moov, moof and meta are cleaned inside). Every other top-level box is blanked: user data,
 * XMP, uuid, a motion photo's clip (mpvd), Samsung's own boxes, C2PA (jumb). An unknown box holds nothing a player needs.
 */
const ISO_TOP = new Set(["ftyp", "styp", "moov", "moof", "mdat", "mfra", "mfro", "sidx", "ssix", "free", "skip", "wide", "junk", "pdin", "emsg", "prft", "pnot"]);

async function planIso(r: Reader, open: Open, kind: "iso" | "heif"): Promise<Plan | null> {
  const edits: Edit[] = [];
  let video: VideoInfo | undefined;
  let location = false;
  let found = false;
  let exif: Uint8Array | undefined;
  /** From `start` to the end of the file is not boxes: cut it (Samsung's SEF trailer, junk after a bad box). */
  const cutRest = async (start: number) => {
    if (r.size <= start) return;
    // The header bytes already read hold no location; the rest is read (when small) or counted as a hit.
    if (r.size > r.pos) location = (await look(r, r.size - r.pos, trailingHasLocation)) || location;
    edits.push({ at: start, remove: r.size - start });
  };
  for (;;) {
    const start = r.pos;
    if (start >= r.size) break;
    if (r.size - start < 8) {
      await cutRest(start);
      break;
    }
    let h = await r.take(8);
    let size = u32be(h, 0);
    if (size === 1) {
      if (start + 16 > r.size) {
        await cutRest(start);
        break;
      }
      const ext = await r.take(8);
      size = u32be(ext, 0) * 2 ** 32 + u32be(ext, 4);
      h = concat([h, ext]);
    } else if (size === 0) size = r.size - start;
    const type = ascii(h, 4);
    if (size < h.length || (start + size > r.size && !ISO_INERT.has(type))) {
      // Not a box, or a box that does not fit and is not media. Everything from here on is cut: it cannot be told from a
      // trailer, and a player cannot read past it either.
      await cutRest(start);
      break;
    }
    if (start + size > r.size) {
      // Media cut off by the end of the file.
      break;
    }
    if (type === "meta" && kind === "heif") {
      // The picture's own items live here: only the Exif and XMP items go.
      if (size > MOOV_MAX) throw new CleanError("this picture's meta box is too big to clean safely", "picture");
      const box = await r.takeBox(h, size - h.length);
      const plan = await planHeifMeta(box, start, h.length, r.size, open);
      edits.push(...plan.edits);
      location ||= plan.location;
      exif ??= plan.exif;
      found = true;
    } else if (type === "moov" || type === "moof") {
      // Read whole (bounded), cleaned in place, and written back over itself: same size, so no offset moves.
      if (size > MOOV_MAX) throw new CleanError(`this video's ${type} box is ${size} bytes, more than can be cleaned safely`, "video");
      const box = await r.takeBox(h, size - h.length);
      const clean = cleanMoov(box);
      if (type === "moov") {
        video = clean.info;
        found = true;
      }
      location ||= clean.location;
      if (clean.changed) edits.push({ at: start, remove: size, insert: box });
    } else if (ISO_TOP.has(type)) {
      await r.skip(size - h.length);
    } else {
      // User data, or a box no player needs: renamed "free" and zeroed (same size, nothing moves). A big one is not read; it
      // counts as holding a location.
      location = (await look(r, size - h.length, (b) => MOV_LOCATION.test(latin1(b)) || textHasGps(b))) || location;
      edits.push({ at: start, remove: size, insert: freeHeader(h), zeros: size - h.length });
    }
  }
  return found ? { edits, video, location, exif } : null;
}

// ---- WebM and Matroska ----

const EBML_HEADER = 0x1a45dfa3;
const MKV_SEGMENT = 0x18538067;
/** Tags, Chapters, Attachments, the movie's Title and a track's Name hold free text and files: a location can sit in any of them. */
const MKV_DROP = new Set([0x1254c367, 0x1043a770, 0x1941a469, 0x7ba9, 0x536e]);
/** Info, Tracks and each TrackEntry are walked into, to reach the Title and the Name. */
const MKV_WALK = new Set([MKV_SEGMENT, 0x1549a966, 0x1654ae6b, 0xae]);
const MKV_LOCATION = /location|gps|latitude|longitude|ISO6709/i;

async function ebmlId(r: Reader): Promise<number> {
  const first = (await r.take(1))[0]!;
  let n = 1;
  while (n <= 4 && !(first & (0x80 >> (n - 1)))) n++;
  if (n > 4) throw new CleanError("this video has an element Bloom can't read, so its hidden details can't be removed", "video");
  let id = first;
  if (n > 1) for (const x of await r.take(n - 1)) id = id * 256 + x;
  return id;
}

/** The size of an element, or null when it is unknown (all value bits set). */
async function ebmlSize(r: Reader): Promise<{ size: number | null; length: number }> {
  const first = (await r.take(1))[0]!;
  let n = 1;
  while (n <= 8 && !(first & (0x80 >> (n - 1)))) n++;
  if (n > 8) throw new CleanError("this video has an element Bloom can't read, so its hidden details can't be removed", "video");
  let v = first & (0xff >> n);
  let ones = v === 0xff >> n;
  if (n > 1) {
    for (const x of await r.take(n - 1)) {
      v = v * 256 + x;
      ones &&= x === 0xff;
    }
  }
  return { size: ones ? null : v, length: n };
}

/** A Void element (0xEC) that fills exactly `total` bytes. */
function voidEdit(at: number, total: number): Edit {
  if (total >= 9) {
    const header = new Uint8Array(9);
    header[0] = 0xec;
    header[1] = 0x01; // an 8-byte size
    let v = total - 9;
    for (let i = 8; i >= 2; i--) {
      header[i] = v % 256;
      v = Math.floor(v / 256);
    }
    return { at, remove: total, insert: header, zeros: total - 9 };
  }
  return { at, remove: total, insert: new Uint8Array([0xec, 0x80 | (total - 2)]), zeros: total - 2 };
}

const MKV_CLUSTER = 0x1f43b675;

async function planWebm(r: Reader): Promise<Plan | null> {
  const edits: Edit[] = [];
  let location = false;
  let segment = false;
  /** The elements being walked into, innermost last. `end` is Infinity for an unknown size. */
  const open: { id: number; end: number }[] = [];
  const unreadable = () => new CleanError("this video has an element Bloom can't read, so its hidden details can't be removed", "video");
  while (r.pos < r.size) {
    while (open.length && r.pos >= open[open.length - 1]!.end) open.pop();
    const start = r.pos;
    let id: number;
    let size: number | null;
    try {
      id = await ebmlId(r);
      size = (await ebmlSize(r)).size;
    } catch (e) {
      // A live recording ends in the middle of an element header: whatever is left is picture data. Anything else that
      // cannot be read is not trusted.
      if (segment && e instanceof Truncated) break;
      throw e;
    }
    if (id === MKV_SEGMENT) segment = true;
    const parentEnd = Math.min(open.length ? open[open.length - 1]!.end : r.size, r.size);
    if (MKV_DROP.has(id) && size === null) throw new CleanError("this video has tags of unknown size, which can't be removed", "video");
    // The Segment, an element of unknown size (a Cluster of a live recording) and the elements that hold the Title and
    // Names are walked into: what is inside them is read as the next elements.
    if (MKV_WALK.has(id) || id === MKV_CLUSTER || size === null) {
      const end = size === null ? Infinity : r.pos + size;
      if (end > parentEnd && parentEnd < r.size) throw unreadable();
      open.push({ id, end });
      continue;
    }
    if (r.pos + size > parentEnd) {
      // Cut off by the end of the file: only inside a cluster, which a recording that stopped leaves half written.
      if (r.pos + size > r.size && open.some((o) => o.id === MKV_CLUSTER)) break;
      throw unreadable();
    }
    if (MKV_DROP.has(id)) {
      if (size > MOOV_MAX) throw new CleanError("this video's tags are too big to clean safely", "video");
      const body = await r.take(size);
      if (MKV_LOCATION.test(latin1(body))) location = true;
      edits.push(voidEdit(start, r.pos - start));
    } else await r.skip(size);
  }
  return segment ? { edits, location } : null;
}

// ---- What a file is ----

export type CleanKind = "jpeg" | "png" | "webp" | "gif" | "heif" | "iso" | "webm";

export interface Sniffed {
  /** A kind with a cleaner, or "unsupported": a picture type that can hold a location and has none. */
  kind: CleanKind | "unsupported";
  mime: string;
  subject: "picture" | "video";
  /** The name of the format, for a refusal of an unsupported one. */
  label?: string;
}

const HEIF_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs", "mif1", "msf1", "mif2", "miaf", "heif", "avif", "avis"]);

/** What a file is from its first bytes (64 or more), whatever its type or name says. Null: nothing we know. */
export function sniffMedia(h: Uint8Array): Sniffed | null {
  const pic = (kind: Sniffed["kind"], mime: string, label?: string): Sniffed => ({ kind, mime, subject: "picture", label });
  const vid = (mime: string, label: string): Sniffed => ({ kind: "unsupported", mime, subject: "video", label });
  if (h[0] === 0xff && h[1] === 0xd8 && h[2] === 0xff) return pic("jpeg", "image/jpeg");
  if (u32be(h, 0) === 0x89504e47 && u32be(h, 4) === 0x0d0a1a0a) return pic("png", "image/png");
  if (ascii(h, 0) === "RIFF" && ascii(h, 8) === "WEBP") return pic("webp", "image/webp");
  if (ascii(h, 0) === "RIFF" && ascii(h, 8) === "AVI ") return vid("video/x-msvideo", "AVI");
  if (ascii(h, 0, 3) === "GIF" && h[3] === 0x38 && (h[4] === 0x37 || h[4] === 0x39) && h[5] === 0x61) return pic("gif", "image/gif");
  if (ascii(h, 4) === "ftyp") {
    const major = ascii(h, 8);
    const size = Math.min(u32be(h, 0), h.length);
    const brands = [major];
    for (let i = 16; i + 4 <= size; i += 4) brands.push(ascii(h, i));
    if (major === "crx ") return pic("unsupported", "image/x-canon-cr3", "Canon CR3");
    if (HEIF_BRANDS.has(major)) {
      const avif = major === "avif" || major === "avis" || (major.startsWith("mi") && brands.includes("avif"));
      return pic("heif", avif ? "image/avif" : /^(mif|msf|heif)/.test(major) ? "image/heif" : "image/heic");
    }
    if (major === "qt  ") return { kind: "iso", mime: "video/quicktime", subject: "video" };
    return { kind: "iso", mime: /^M4[ABP] $/.test(major) ? "audio/mp4" : "video/mp4", subject: "video" };
  }
  if (u32be(h, 0) === EBML_HEADER) return { kind: "webm", mime: "video/webm", subject: "video" };
  // A QuickTime movie can start with any box: the file has no ftyp.
  // Text that has "free" at bytes 4 to 7 is not one: the box size before it has a zero byte.
  if (["moov", "mdat", "free", "wide", "skip", "pnot", "junk"].includes(ascii(h, 4)) && looksBinary(h)) return { kind: "iso", mime: "video/quicktime", subject: "video" };
  // Video containers with no cleaner. A TypeScript file named .ts is not one: a sync byte every 188 (or 192) bytes is.
  if (ascii(h, 0, 3) === "FLV" && h[3] === 1) return vid("video/x-flv", "FLV");
  if (u32be(h, 0) === 0x3026b275 && u32be(h, 4) === 0x8e66cf11) return vid("video/x-ms-wmv", "WMV");
  if (h[0] === 0 && h[1] === 0 && h[2] === 1 && (h[3] === 0xba || h[3] === 0xb3)) return vid("video/mpeg", "MPEG");
  if (h.length >= 377 && h[0] === 0x47 && h[188] === 0x47 && h[376] === 0x47) return vid("video/mp2t", "MPEG-TS");
  if (h.length >= 389 && h[4] === 0x47 && h[196] === 0x47 && h[388] === 0x47) return vid("video/mp2t", "MTS");
  // TIFF and the RAW formats built on it (DNG, NEF, CR2, ARW, ORF, RW2), JPEG XL, other RAW and layered files.
  const ii = h[0] === 0x49 && h[1] === 0x49 && ((h[3] === 0 && [0x2a, 0x2b, 0x55, 0x1a].includes(h[2]!)) || (h[2] === 0x52 && (h[3] === 0x4f || h[3] === 0x53)));
  const mm = h[0] === 0x4d && h[1] === 0x4d && ((h[2] === 0 && (h[3] === 0x2a || h[3] === 0x2b)) || (h[2] === 0x4f && h[3] === 0x52));
  const tiff = ii || mm;
  if (tiff) return pic("unsupported", "image/tiff", "TIFF or RAW");
  if ((h[0] === 0xff && h[1] === 0x0a) || (u32be(h, 0) === 12 && ascii(h, 4) === "JXL ")) return pic("unsupported", "image/jxl", "JPEG XL");
  if (ascii(h, 0, 15) === "FUJIFILMCCD-RAW" || (h[0] === 0 && ascii(h, 1, 3) === "MRM") || ascii(h, 0) === "FOVb" || ascii(h, 0) === "8BPS") return pic("unsupported", "image/x-raw", "RAW");
  return null;
}

/** Declared picture types that carry no EXIF-like block we need to read (drawings, plain bitmaps, icons). */
const PLAIN_IMAGE = /^image\/(svg\+xml|bmp|x-ms-bmp|x-icon|vnd\.microsoft\.icon|x-win-bitmap)$/;

const KEEP_FAMILY: Record<CleanKind, RegExp> = {
  jpeg: /^image\/jpeg$/,
  png: /^image\/png$/,
  webp: /^image\/webp$/,
  gif: /^image\/gif$/,
  heif: /^image\/(heic|heif|avif)$/,
  iso: /^(video|audio)\//,
  webm: /^video\//,
};

/** The type to store a file under: the declared one when it fits what the bytes are, else what the bytes say. */
export function mimeFor(kind: CleanKind, sniffed: string, declared: string): string {
  if (!KEEP_FAMILY[kind].test(declared)) return sniffed;
  // A declared heic for AVIF bytes (or the reverse) is mended; heif for heic bytes is kept.
  if (kind === "heif" && (declared === "image/avif") !== (sniffed === "image/avif")) return sniffed;
  return declared;
}

export interface MediaPlan extends Plan {
  kind: CleanKind;
  /** The type the file is stored as. */
  mime: string;
  subject: "picture" | "video";
}

/** Whether the first bytes of a file are not text: a NUL or a control character other than tab, line feed, form feed, return and escape. */
const looksBinary = (h: Uint8Array) => h.some((x) => x < 9 || (x > 13 && x < 32 && x !== 27));

const SUBJECT = (declared: string): "picture" | "video" => (declared.startsWith("video/") ? "video" : "picture");

/**
 * What it takes to clean a file, found by reading its bytes: null when it is not a picture or video we handle (it is
 * stored as it is); a plan otherwise. Throws CleanError when the file should be cleaned and can't be: a type with no
 * cleaner, a file that doesn't parse, a box too big to read.
 */
export async function planMedia(open: Open, size: number, declared: string): Promise<MediaPlan | null> {
  const head = await readAt(open, 0, Math.min(size, 512)).catch(() => new Uint8Array(0));
  const sniffed = sniffMedia(head);
  if (!sniffed) {
    if (PLAIN_IMAGE.test(declared)) return null;
    // A type that only the sender claimed: text, or an empty file, is a plain file (a TypeScript file arrives as video/mp2t).
    // Binary bytes that are no picture or video Bloom knows are refused: they could be one it cannot read.
    if (/^(image|video)\//.test(declared) && looksBinary(head)) {
      throw new CleanError(`a ${declared} file that Bloom can't read, so its hidden details can't be removed`, SUBJECT(declared), "other");
    }
    return null;
  }
  // An M4A, M4B or M4P is audio: it is not a video, and a voice recording is not refused for being one.
  if (sniffed.mime === "audio/mp4" && !declared.startsWith("video/")) return null;
  if (sniffed.kind === "unsupported") {
    if (sniffed.subject === "video" && declared.startsWith("audio/")) return null; // for example a WMA
    throw new CleanError(`${sniffed.mime} files can hold a location Bloom can't remove`, sniffed.subject, "format", sniffed.label);
  }
  const kind = sniffed.kind;
  const r = new Reader(open, size);
  try {
    const plan = await (kind === "jpeg" ? planJpeg(r) : kind === "png" ? planPng(r) : kind === "webp" ? planWebp(r) : kind === "gif" ? planGif(r) : kind === "webm" ? planWebm(r) : planIso(r, open, kind));
    if (!plan) throw new CleanError("this file doesn't parse, so its hidden details can't be removed", sniffed.subject);
    // Edits that overlap would be a bug in a plan made from a hostile file: refuse it, don't fail on it.
    sortEdits(plan.edits);
    return { ...plan, kind, mime: mimeFor(kind, sniffed.mime, declared), subject: sniffed.subject };
  } catch (e) {
    if (e instanceof CleanError) throw e;
    // A truncated or malformed file.
    throw new CleanError(`this file doesn't parse (${e instanceof Error ? e.message : e}), so its hidden details can't be removed`, sniffed.subject);
  } finally {
    await r.close();
  }
}

/**
 * The message a person reads when a client did not send a picture or video. The words for a location are the ones the
 * clients showed before this had reasons.
 */
export function refusalMessage(subject: "picture" | "video", reason: RefusalReason = "location", label?: string): string {
  if (reason === "format") return `Bloom can't send ${label ?? "this kind of"} files, because it can't remove their hidden details like the location. This ${subject} wasn't sent.`;
  if (reason === "other") return `Bloom couldn't check this ${subject} for hidden details like its location, so it wasn't sent.`;
  return `This ${subject} has location data Bloom can't remove, so it wasn't sent.`;
}

/** The message a person reads when the server refused to store a picture or video. */
export function cleanRefusal(subject: "picture" | "video", reason: RefusalReason = "other", label?: string): string {
  if (reason === "format") return `Bloom can't store ${label ?? "this kind of"} files, because it can't remove their hidden details like the location. This ${subject} wasn't stored.`;
  return `This ${subject}'s hidden details (like its location) couldn't be removed, so it wasn't stored.`;
}

/**
 * A place written out, wherever it sits in a file: a GPS field name, ISO 6709 (the text Apple and Android write: `+40.7128-074.0060/`
 * or `+4042.768-07400.360/`). A last check on a cleaned copy, beside the structure the cleaner knows: it finds the place in
 * a spot the cleaner did not think of. Patterns of four bytes are not searched for: they turn up by chance in sample data.
 */
const RAW_LOCATION = /GPSLatitude|GPSLongitude|GPSAltitude|GPSPosition|exif:GPS|ISO6709|[+-]\d{2,3}\.\d{3,}[+-]\d{3}\.\d{3,}|[+-]\d{4}\.\d{2,}[+-]\d{5}\.\d{2,}/;
export const rawHasLocation = (b: Uint8Array) => RAW_LOCATION.test(latin1(b));

/**
 * Whether the plan of a cleaned copy finds nothing left to do: no location, and (except for a JPEG, whose orientation segment
 * is written again on every pass) no edit. A copy the cleaner would change again was not cleaned.
 */
export const planIsClean = (plan: MediaPlan) => !plan.location && plan.edits.length === 0;

/** A clean copy of a file in memory, and whether the original held a location. */
export async function cleanBytes(bytes: Uint8Array, declared: string): Promise<{ bytes: Uint8Array; plan: MediaPlan } | null> {
  const plan = await planMedia(openBytes(bytes), bytes.length, declared);
  if (!plan) return null;
  const out = applyEditsToBytes(bytes, plan.edits);
  if (rawHasLocation(out)) throw new CleanError("the cleaned copy still shows a location", plan.subject, "location");
  // The read-back: the cleaner finds nothing left to cut, so no tag outside the allowlist is in the copy.
  const again = await planMedia(openBytes(out), out.length, plan.mime);
  if (again && !planIsClean(again)) throw new CleanError("the cleaned copy still holds data that should be gone", plan.subject, "other");
  return { bytes: out, plan };
}

/**
 * Reads a file and says whether it holds a location. Run on the cleaned copy, it is the check that nothing was missed.
 * It looks at the location the plan found, not at the plan's edits: a JPEG's orientation segment is rewritten on every
 * pass, so a clean file can still have edits.
 */
export async function hasLocation(bytes: Uint8Array, declared: string): Promise<boolean> {
  const plan = await planMedia(openBytes(bytes), bytes.length, declared);
  return plan ? plan.location || rawHasLocation(bytes) : false;
}
